import { Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { mdiChevronDown, mdiChevronUp, mdiPlus } from '@mdi/js';
import Icon from '@mdi/react';
import ActionContainer from 'components/ActionContainer/ActionContainer';
import Button from 'components/Button/Button';
import Loading from 'components/Loading/Loading';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProjectCompetencyTypeItem } from './ProjectCompetencyTypeItem';

const useStyles = makeStyles((theme) => ({
  emptyState: {
    padding: theme.spacing(1, 0),
  },
}));

export const ProjectCompetencyTypesSection = ({
  loading,
  isLoadingCompetencyTypes,
  open,
  setOpen,
  competencyTypes,
  onAddCompetencyType,
  onChangeCompetencyType,
  onDeleteCompetencyType,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const [openCompetencyTypesArray, setOpenCompetencyTypesArray] = useState([]);

  const activeCompetencyTypes = useMemo(
    () => competencyTypes.filter((competencyType) => !competencyType.deletedAt),
    [competencyTypes]
  );

  const competencyTypeNameCounts = useMemo(() => {
    return activeCompetencyTypes.reduce((counts, competencyType) => {
      const normalizedName = competencyType.name?.toString().trim().toLowerCase();

      if (!normalizedName) {
        return counts;
      }

      return {
        ...counts,
        [normalizedName]: (counts[normalizedName] || 0) + 1,
      };
    }, {});
  }, [activeCompetencyTypes]);

  useEffect(() => {
    setOpenCompetencyTypesArray((currentOpenCompetencyTypesArray) =>
      currentOpenCompetencyTypesArray.filter((id) =>
        activeCompetencyTypes.some((competencyType) => competencyType.id === id)
      )
    );
  }, [activeCompetencyTypes]);

  const handleAddCompetencyType = () => {
    const createdCompetencyTypeId = onAddCompetencyType();

    if (!createdCompetencyTypeId) {
      return;
    }

    setOpenCompetencyTypesArray((currentOpenCompetencyTypesArray) => [
      ...currentOpenCompetencyTypesArray,
      createdCompetencyTypeId,
    ]);
  };

  return (
    <ActionContainer
      title={t('project.competencies')}
      closable
      open={open}
      setOpen={setOpen}
    >
      {isLoadingCompetencyTypes ? (
        <Grid item xs={12}>
          <Loading tiny />
        </Grid>
      ) : activeCompetencyTypes.length > 0 ? (
        <>
          <Grid container item spacing={1} justifyContent="flex-end">
            <Grid item xs="auto">
              {openCompetencyTypesArray.length ? (
                <Button
                  color="primary"
                  outline
                  disabled={loading}
                  onClick={() => setOpenCompetencyTypesArray([])}
                >
                  {t('project_dialog.button_close_all')}
                  <Icon path={mdiChevronUp} size={1} />
                </Button>
              ) : (
                <Button
                  color="primary"
                  outline
                  disabled={loading}
                  onClick={() =>
                    setOpenCompetencyTypesArray(
                      activeCompetencyTypes.map((competencyType) => competencyType.id)
                    )
                  }
                >
                  {t('project_dialog.button_open_all')}
                  <Icon path={mdiChevronDown} size={1} />
                </Button>
              )}
            </Grid>
          </Grid>
          {activeCompetencyTypes.map((competencyType) => (
            <Grid container item key={competencyType.id}>
              <ProjectCompetencyTypeItem
                competencyType={competencyType}
                loading={loading || isLoadingCompetencyTypes}
                hasDuplicateName={
                  competencyType.isNew &&
                  Boolean(competencyType.name?.toString().trim()) &&
                  competencyTypeNameCounts[
                    competencyType.name.toString().trim().toLowerCase()
                  ] > 1
                }
                openCompetencyTypesArray={openCompetencyTypesArray}
                setOpenCompetencyTypesArray={setOpenCompetencyTypesArray}
                onChangeCompetencyType={onChangeCompetencyType}
                onDeleteCompetencyType={onDeleteCompetencyType}
              />
            </Grid>
          ))}
        </>
      ) : (
        <Grid item xs={12}>
          <Typography className={classes.emptyState}>
            {t('common.no_options')}
          </Typography>
        </Grid>
      )}
      <Grid item xs={12} style={{ marginTop: 8 }}>
        <Button
          color="primary"
          fullWidth
          disabled={loading || isLoadingCompetencyTypes}
          onClick={handleAddCompetencyType}
        >
          <Icon path={mdiPlus} size={1} />
          {t('project_dialog.button_add_new')}
        </Button>
      </Grid>
    </ActionContainer>
  );
};

ProjectCompetencyTypesSection.propTypes = {
  loading: PropTypes.bool,
  isLoadingCompetencyTypes: PropTypes.bool,
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  competencyTypes: PropTypes.array,
  onAddCompetencyType: PropTypes.func,
  onChangeCompetencyType: PropTypes.func,
  onDeleteCompetencyType: PropTypes.func,
};

ProjectCompetencyTypesSection.defaultProps = {
  loading: false,
  isLoadingCompetencyTypes: false,
  open: false,
  setOpen: () => {},
  competencyTypes: [],
  onAddCompetencyType: () => {},
  onChangeCompetencyType: () => {},
  onDeleteCompetencyType: () => {},
};
